'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { toast } from 'sonner'
import { useAuthStore } from '@/lib/stores/auth'
import { useOrganizationStore } from '@/lib/stores/organization'
import {
  TWO_FACTOR_ENROLL_REQUEST,
  TWO_FACTOR_REQUIRED_CODE,
} from '@/lib/auth/two-factor-requirement'

interface OrganizationLoaderProps {
  children: React.ReactNode
}

const MAX_RETRIES = 3
const RETRY_DELAY_MS = 2000

export function OrganizationLoader({ children }: OrganizationLoaderProps) {
  const pathname = usePathname()
  const isPublicRoute =
    pathname.startsWith('/open/') ||
    pathname.startsWith('/customer-portal/') ||
    pathname === '/login' ||
    pathname.startsWith('/login/')
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const loadFromDB = useOrganizationStore((s) => s.loadFromDB)
  const dbLoaded = useOrganizationStore((s) => s._dbLoaded)
  const dbLoadError = useOrganizationStore((s) => s._dbLoadError)
  const dbLoadErrorCode = useOrganizationStore((s) => s._dbLoadErrorCode)
  const loadingRef = useRef(false)
  const retryCount = useRef(0)
  const [retryTick, setRetryTick] = useState(0)
  const errorShown = useRef(false)

  /** 再試行タイマー。アンマウント後に setRetryTick が走らないよう cleanup で必ず止める。 */
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (dbLoaded) return
    if (loadingRef.current) return
    if (isLoading) return
    if (!isAuthenticated) return
    if (isPublicRoute) return
    if (retryCount.current >= MAX_RETRIES) return

    loadingRef.current = true
    retryCount.current += 1

    let cancelled = false
    loadFromDB().finally(() => {
      loadingRef.current = false
      // 🔴 アンマウント後は何もしない（台帳 USEEFFECT-CLEANUP）。読み込みは数秒かかることがあり、
      // 画面を離れた後に戻ってくると、消えたコンポーネントの state を書き換えたり、
      // 誰も見ていない画面に向けてトーストを出したりする。
      if (cancelled) return
      const { _dbLoaded } = useOrganizationStore.getState()
      if (!_dbLoaded) {
        if (retryCount.current < MAX_RETRIES) {
          retryTimerRef.current = setTimeout(() => setRetryTick((t) => t + 1), RETRY_DELAY_MS)
        } else {
          // エラーが設定されていない場合（401サイレントスキップ等）はトースト表示
          const { _dbLoadError: err } = useOrganizationStore.getState()
          if (!err) {
            toast.warning(
              'ユーザーリストの読み込みに失敗しました。ページを再読み込みしてください。'
            )
          }
        }
      }
    })
    return () => {
      cancelled = true
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }
  }, [isAuthenticated, isLoading, loadFromDB, isPublicRoute, dbLoaded, retryTick])

  useEffect(() => {
    if (!dbLoadError || errorShown.current) return
    if (isPublicRoute) return
    errorShown.current = true
    // 2段階認証の登録へ誘導中で断られたときは、覆いと同じ登録のお願いの1文だけ（AUTH-01 補足7）
    toast.error(
      dbLoadErrorCode === TWO_FACTOR_REQUIRED_CODE
        ? TWO_FACTOR_ENROLL_REQUEST
        : `組織データの読み込みに失敗しました: ${dbLoadError}`
    )
  }, [dbLoadError, dbLoadErrorCode, isPublicRoute])

  return <>{children}</>
}
